import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import EmailDraftFields from '../../components/EmailDraftFields';
import {
  fetchVendorPoEmailDrafts,
  poAttachmentLabel,
  sendVendorPurchaseOrdersBatch,
  vendorPoBatchSendErrorMessage,
  vendorPoSendErrorMessage,
  vendorPoSendProblems,
  type BatchSendResult,
  type VendorPoEmailDraft,
} from '../../lib/vendorPurchaseOrderApi';

// "Send unsent POs": one email per vendor, each shown in full and sent as shown (D180,
// BUSINESS_RULES#outgoing-email-as-shown). A vendor with no address stays here until one is typed.

interface EmailRow extends VendorPoEmailDraft {
  key: string;
  to: string;
}

export interface VendorPoEmailsDialogProps {
  poIds: string[];
  format: 'pdf' | 'excel';
  onClose: () => void;
  /** After each send, with what the server did. */
  onSent: (result: BatchSendResult) => void;
}

const VendorPoEmailsDialog: React.FC<VendorPoEmailsDialogProps> = ({ poIds, format, onClose, onSent }) => {
  const [rows, setRows] = useState<EmailRow[] | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const poIdsKey = poIds.join(',');
  useEffect(() => {
    let stale = false;
    fetchVendorPoEmailDrafts(poIdsKey.split(','))
      .then(({ drafts, skipped }) => {
        if (stale) return;
        setRows(drafts.map((draft) => ({ ...draft, key: draft.poIds.join(','), to: draft.recipients.join(', ') })));
        setProblems(vendorPoSendProblems({ sent: [], notRecorded: [], failed: [], skipped }));
      })
      .catch((e: unknown) => {
        if (!stale) setError(vendorPoSendErrorMessage(e, 'The emails could not be prepared, so nothing can be sent yet. Close this and try again.'));
      });
    return () => {
      stale = true;
    };
  }, [poIdsKey]);

  const patchRow = (key: string, patch: Partial<EmailRow>) =>
    setRows((prev) => (prev || []).map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const ready = (rows || []).filter((row) => row.to.trim());

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await sendVendorPurchaseOrdersBatch({
        emails: ready.map((row) => ({ poIds: row.poIds, draftVersion: row.draftVersion, recipients: row.to.split(/[,;\s]+/).filter(Boolean), subject: row.subject, body: row.body })),
        format,
      });
      onSent(result);
      // A failed email can be tried again; one sent, unrecorded or skipped cannot, as its text no longer holds.
      const retry = new Set(result.failed.map((row) => row.id));
      const left = (rows || []).filter((row) => !ready.includes(row) || row.poIds.some((id) => retry.has(id)));
      const nowProblems = vendorPoSendProblems(result);
      setRows(left);
      setProblems(nowProblems);
      if (left.length === 0 && nowProblems.length === 0) onClose();
    } catch (e: unknown) {
      setError(vendorPoBatchSendErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      <div role="dialog" aria-modal="true" aria-label="Send unsent POs" className="bg-white rounded-lg relative w-full max-w-[720px] max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">Send unsent POs</h2>
          <p className="mt-1 text-xs text-slate-500">One email per vendor. Each goes exactly as it reads here.</p>
        </div>

        <div className="flex-1 px-6 pb-4">
          {!rows && !error && <p role="status" className="mb-4 text-sm text-slate-600">Preparing the emails…</p>}
          {error && <p role="alert" className="mb-4 text-sm text-red-600">{error}</p>}
          {problems.map((problem) => (
            <p key={problem} className="mb-2 text-xs text-amber-700">{problem}</p>
          ))}
          {rows?.map((row, index) => (
            <section key={row.key} aria-label={row.vendorName || row.poNumbers.join(', ')} className="mb-4 rounded-md border border-slate-200 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">{row.vendorName || 'No vendor name'}</h3>
              <div className="mb-4">
                <label htmlFor={`po-email-${index}-to`} className="block mb-2 text-sm font-medium text-slate-900">To</label>
                <input
                  id={`po-email-${index}-to`}
                  type="text"
                  value={row.to}
                  disabled={sending}
                  onChange={(e) => patchRow(row.key, { to: e.target.value })}
                  placeholder="vendor@supplier.com"
                  className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
                />
                {!row.to.trim() && (
                  <p className="mt-1 text-xs text-amber-700">No email address for this vendor. Type one to send it; it will not go without.</p>
                )}
              </div>
              <EmailDraftFields
                idPrefix={`po-email-${index}`}
                subject={row.subject}
                onSubjectChange={(subject) => patchRow(row.key, { subject })}
                body={row.body}
                onBodyChange={(body) => patchRow(row.key, { body })}
                attachment={poAttachmentLabel(row.poNumbers, format)}
                disabled={sending}
              />
            </section>
          ))}
        </div>

        <div className="p-6 pt-0 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="flex-1 bg-white border border-[#D7DBD9] rounded-lg h-10 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || ready.length === 0}
            className="flex-1 bg-primary-500 rounded-lg h-10 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
          >
            {sending ? 'Sending…' : `Send ${ready.length} email${ready.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default VendorPoEmailsDialog;
